import React, { useEffect, useState } from "react";
import {
  NiaOpportunityBestBenchmark,
  NiaOpportunityCommodityFilters,
  NiaOpportunityPeerGrowthBenchmark,
  NiaOpportunityTableData,
} from "../../services/interfaces/INiaOpportunity";
import {
  GrowthPerecentageHeaders,
  NIAOpportunityLabels as LABELS,
} from "./NiaOpportunityConstants";
import NiaOppVendorsList from "./nia-opp-vendor-list/NiaOppVendorsList";
import NiaOppDetailsSection from "./nia-opp-details-section/NiaOppDetailsSection";
import {
  useNiaOpportunityPeerBenchmarkDetails,
  useNiaOpportunityVendorDetails,
  useNiaOpportunityVendorsList,
} from "../../react-query/hooks/HNiaOpportunity";
import { Loader } from "rsuite";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { sortCommodityFirst } from "../../utils/helpers/commodityOrder";

const NIAOpportunity = () => {
  //Left Section
  const [vendorSelected, setVendorSelected] =
    useState<NiaOpportunityTableData>();

  const [vendorNameSelected, setVendorNameSelected] = useState<string | null>(
    null
  );

  //Right Section

  const [niaBaseOpportunity, setNiaBaseOpportunity] = useState<number>(0);
  const [niaGrowthOpportunity, setNiaGrowthOpportunity] = useState<number>(0);
  const [sliderPercentage, setSliderPercentage] = useState<number>(0);

  const [selectedCommodityFilter, setSelectedCommodityFilter] =
    useState<NiaOpportunityCommodityFilters>();
  const [commodityFiltersList, setCommodityFiltersList] = useState<
    NiaOpportunityCommodityFilters[]
  >([]);

  const [peerBenchmarkList, setPeerBenchmarkList] =
    useState<NiaOpportunityPeerGrowthBenchmark[]>();

  const [selectedBaseRebate, setSelectedBaseRebate] = useState<number>(0);
  const [selectedSpend, setSelectedSpend] = useState<number>(0);

  const [peerBenchmark, setPeerBenchmark] =
    useState<NiaOpportunityPeerGrowthBenchmark>();

  const [triggereGetPeerbenchmarkDetails, setTriggereGetPeerbenchmarkDetails] =
    useState(false);

  const [originalBestBenchmark, setOriginalBestBenchmark] =
    useState<NiaOpportunityBestBenchmark[]>();

  const [bestBenchmark, setBestBenchmark] =
    useState<NiaOpportunityBestBenchmark[]>();

  const [editMode, setEditMode] = useState<boolean>(false);

  const [niaBaseDiff, setNiaBaseDiff] = useState<number | null>(null);

  // API hooks
  const { vendorsListResponse, isVendorsListLoading } =
    useNiaOpportunityVendorsList();

  const { vendorDetailsResponse, isVendorDetailsLoading } =
    useNiaOpportunityVendorDetails(vendorNameSelected);

  const { peerBenchmarkDetailsResponse, isPeerBenchmarkDetailsLoading } =
    useNiaOpportunityPeerBenchmarkDetails(
      vendorNameSelected,
      selectedCommodityFilter?.commodity || null,
      triggereGetPeerbenchmarkDetails
    );

  useEffect(() => {
    if (
      vendorDetailsResponse &&
      Object.keys(vendorDetailsResponse).length !== 0
    ) {
      const defaultCommodity = sortCommodityFirst(
        vendorDetailsResponse?.commodity,
      )[0];
      setNiaBaseOpportunity(
        vendorDetailsResponse?.opportunityData[0]?.NIA_Base_Opportunity || 0
      );
      setNiaGrowthOpportunity(
        vendorDetailsResponse?.opportunityData[0]?.NIA_Growth_Opportunity || 0
      );
      setSliderPercentage(vendorDetailsResponse?.slider_percentage);
      setSelectedCommodityFilter(defaultCommodity);
      setCommodityFiltersList(vendorDetailsResponse?.commodity);
      setOriginalBestBenchmark(
        JSON.parse(JSON.stringify(vendorDetailsResponse?.bestBenchmarkData))
      );
      setBestBenchmark(
        JSON.parse(JSON.stringify(vendorDetailsResponse?.bestBenchmarkData))
      );
      setPeerBenchmarkList(vendorDetailsResponse.peerBenchmarkData);
      setSelectedBaseRebate(defaultCommodity.base_rebate);
      setSelectedSpend(defaultCommodity.spend);
      if (
        vendorDetailsResponse?.peerBenchmarkData[0]?.peer_base_rebate >=
        defaultCommodity?.base_rebate
      )
        setPeerBenchmark(
          peerBenchmarkDetailsResponse?.length
            ? peerBenchmarkDetailsResponse[0]
            : undefined
        );
      setTriggereGetPeerbenchmarkDetails(true);
    }
  }, [vendorDetailsResponse]);

  const resetHandler = () => {
    setNiaBaseDiff(null);
    if (originalBestBenchmark) {
      setBestBenchmark(JSON.parse(JSON.stringify(originalBestBenchmark)));
      // setPeerBenchmark(undefined);
      setNiaBaseOpportunity(
        vendorDetailsResponse?.opportunityData[0]?.NIA_Base_Opportunity || 0
      );
      setNiaGrowthOpportunity(
        vendorDetailsResponse?.opportunityData[0]?.NIA_Growth_Opportunity || 0
      );
      peerBenchmarkList && setPeerBenchmark(peerBenchmarkList[0]);
      setEditMode(false);
    }
  };

  useEffect(() => {
    if (peerBenchmarkDetailsResponse && triggereGetPeerbenchmarkDetails) {
      setPeerBenchmarkList(peerBenchmarkDetailsResponse);
      if (
        peerBenchmarkDetailsResponse[0]?.peer_base_rebate >= selectedBaseRebate
      )
        setPeerBenchmark(
          peerBenchmarkDetailsResponse?.length
            ? peerBenchmarkDetailsResponse[0]
            : undefined
        );
    }
  }, [peerBenchmarkDetailsResponse]);

  const handleVendorSelected = (rowData: NiaOpportunityTableData) => {
    setNiaBaseDiff(null);
    if (rowData !== null) {
      setVendorNameSelected(rowData.vendor);
      setVendorSelected(rowData);
      if (peerBenchmark) setPeerBenchmark(undefined);
      setEditMode(false);
    }
  };

  const handleCategorySelected = (categorySelected: string | null) => {
    if (categorySelected === null) return;
    setSelectedBaseRebate(
      commodityFiltersList?.find((item) => item.commodity === categorySelected)
        ?.base_rebate ?? 0
    );
    setSelectedSpend(
      commodityFiltersList?.find((item) => item.commodity === categorySelected)
        ?.spend ?? 0
    );
    setNiaBaseOpportunity(
      vendorDetailsResponse?.opportunityData[0]?.NIA_Base_Opportunity || 0
    );
    setPeerBenchmark(undefined);
    setSelectedCommodityFilter(
      commodityFiltersList.find((comm) => comm.commodity === categorySelected)
    );
    setNiaBaseDiff(null);
    setTriggereGetPeerbenchmarkDetails(true);
  };

  const handleSubmitChanges = () => {
    if (
      bestBenchmark &&
      vendorDetailsResponse?.rangeVal?.length !== 0 &&
      vendorSelected?.spend
    ) {
      const bestBenchmarkValue = bestBenchmark[1][
        `${vendorDetailsResponse?.rangeVal}` as keyof NiaOpportunityBestBenchmark
      ] as number;
      const currentBenchmarkValue = bestBenchmark[0][
        `${vendorDetailsResponse?.rangeVal}` as keyof NiaOpportunityBestBenchmark
      ] as number;

      if (bestBenchmarkValue - currentBenchmarkValue < 0) return;

      const calculatedGrowthValue = Number(
        ((bestBenchmarkValue - currentBenchmarkValue) / 100) *
        vendorSelected?.spend
      );

      setNiaGrowthOpportunity(calculatedGrowthValue);
    }
    if (peerBenchmark) {
      if (
        peerBenchmarkList?.findIndex(
          (peerBenchmarkObj) =>
            peerBenchmark.peer_group === peerBenchmarkObj.peer_group
        ) === 0
      ) {
        setNiaBaseOpportunity(
          vendorDetailsResponse?.opportunityData[0]?.NIA_Base_Opportunity || 0
        );
        setNiaBaseDiff(null);
        return;
      }
      if (
        peerBenchmarkList &&
        peerBenchmarkList?.length !== 0 &&
        peerBenchmark.peer_base_rebate === peerBenchmarkList[0].peer_base_rebate
      ) {
        setNiaBaseOpportunity(
          vendorDetailsResponse?.opportunityData[0]?.NIA_Base_Opportunity || 0
        );
        setNiaBaseDiff(null);
        return;
      }
      const niaBaseOpportunityCalculated =
        ((peerBenchmark.peer_base_rebate - selectedBaseRebate) / 100) *
        selectedSpend;

      let getBestBaseOpportunity = 0;

      vendorDetailsResponse?.commodity
        ?.filter(
          (commodity) =>
            commodity.commodity !== selectedCommodityFilter?.commodity
        )
        .map(
          (commodity) =>
            (getBestBaseOpportunity += commodity.best_base_opportunity)
        );

      let baseDiff = getBestBaseOpportunity + niaBaseOpportunityCalculated;

      let newDiff = parseFloat(vendorDetailsResponse?.opportunityData[0]?.NIA_Base_Opportunity?.toFixed(2)) - Math.abs(baseDiff);
      if (
        niaBaseOpportunityCalculated !== 0 &&
        niaBaseOpportunityCalculated.toFixed(2) !== "0.00"
      ) {
        setNiaBaseDiff(newDiff);
      }

      setNiaBaseOpportunity(baseDiff);
    }
  };

  const handleExport = async () => {
    const workbook = new ExcelJS.Workbook();

    // Common Styling for Header and Footer
    const headerFooterStyle = {
      font: { bold: true, color: { argb: "00000000" } }, // White Font
      fill: {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFFFEB3B" }, // Yellow Background
      },
    };

    if (bestBenchmark) {
      // Table 1
      const worksheet1 = workbook.addWorksheet("Growth");

      worksheet1.columns = GrowthPerecentageHeaders.map((column, index) => ({
        header: column.header,
        key: column.field,
        width: index === 0 ? 30 : 15,
      }));

      const formattedData = bestBenchmark.map((row) => row);
      worksheet1.addRows(formattedData);

      // Style Header Row
      const headerRow1 = worksheet1.getRow(1);
      headerRow1.eachCell((cell) => {
        cell.font = headerFooterStyle.font;
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFFFEB3B" }, // Yellow Background
        };
      });

      // Add Footer Row
      const footerRow1 = worksheet1.addRow({
        vendor: "NIA Growth Opportunity",
        "0%": "",
        "100%": "",
        "102.5%": "",
        "105%": "",
        "107.5%": "",
        "110%": "",
        "112.5%": "",
        "115%": "",
        "117.5%": "",
        "120%": "",
        "125%+": `$${niaGrowthOpportunity.toFixed(3)}M`,
      });
      footerRow1.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFF" },
        };
      });
    }

    if (peerBenchmarkList && peerBenchmarkList.length !== 0) {
      const worksheet2 = workbook.addWorksheet("Base");
      worksheet2.columns = [
        { header: "Supplier", key: "vendor", width: 50 },
        { header: "Category", key: "category", width: 40 },
        { header: "Peer", key: "peer_group", width: 50 },
        { header: "Spend", key: "spend", width: 20 },
        { header: "Base Rebate", key: "base_rebate", width: 20 },
        { header: "Peer Base Rebate", key: "peer_base_rebate", width: 20 },
      ];
      const formattedData = peerBenchmarkList.map(
        ({ spend, peer_group, peer_base_rebate }) => ({
          vendor: vendorNameSelected,
          category: selectedCommodityFilter?.commodity,
          peer_group: peer_group,
          spend: "$ " + spend?.toFixed(2) + " M",
          base_rebate: selectedCommodityFilter?.base_rebate,
          peer_base_rebate: peer_base_rebate,
        })
      );
      worksheet2.addRows(formattedData);

      // Style Header Row
      const headerRow2 = worksheet2.getRow(1);
      headerRow2.eachCell((cell) => {
        cell.font = headerFooterStyle.font;
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFFFEB3B" },
        };
      });

      let calculateNiaBaseOpportunity = niaBaseOpportunity;
      if (peerBenchmark) {
        calculateNiaBaseOpportunity =
          ((peerBenchmarkList[0].peer_base_rebate - selectedBaseRebate) / 100) *
          selectedSpend;
      }

      //Footer Row
      const footerRow2 = worksheet2.addRow({
        vendor: "NIA Base Opportunity",
        category: "",
        peer_group: "",
        spend: "",
        base_rebate: "",
        peer_base_rebate: `$${calculateNiaBaseOpportunity.toFixed(3)}M`,
      });
      footerRow2.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFF" },
        };
      });
    }

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `${vendorNameSelected}.xlsx`);
  };

  const isValidInputBenchmark = () => {
    if (bestBenchmark && vendorDetailsResponse?.rangeVal?.length !== 0) {
      const bestBenchmarkValue = bestBenchmark[1][
        `${vendorDetailsResponse?.rangeVal}` as keyof NiaOpportunityBestBenchmark
      ] as number;
      const currentBenchmarkValue = bestBenchmark[0][
        `${vendorDetailsResponse?.rangeVal}` as keyof NiaOpportunityBestBenchmark
      ] as number;

      return bestBenchmarkValue - currentBenchmarkValue >= 0;
    }
    return false;
  };

  return (
    <>
      {isVendorsListLoading || isVendorDetailsLoading ? (
        <Loader
          backdrop
          content="Loading..."
          size="md"
          vertical
          className="text-black font-bold"
          style={{ opacity: 0.6, zIndex: 1105 }}
        />
      ) : (
        <div className="flex flex-col justify-stretch items-stretch h-full volume-allocation nia-container">
          <div className="grow shrink-0 basis-[17%] ps-[3%] pr-[5%] pt-[1.6%] flex flex-col flex-nowrap justify-start items-start gap-0 shadow-normal relative">
            <div className="flex justify-between items-center w-full">
              <div className="font-normal text-[2vw]">
                {LABELS.NIAOpportunityTitle}
              </div>
            </div>
          </div>
          <div className="bg-[#F1F1F1] grow shrink p-[2%] pr-[5%] flex flex-row flex-nowrap justify-between items-start gap-6 h-full w-full">
            <NiaOppVendorsList
              vendorList={vendorsListResponse?.data}
              spendYear={vendorsListResponse?.spend_year}
              vendorSelected={vendorSelected}
              setVendorSelected={setVendorSelected}
              handleVendorSelected={handleVendorSelected}
            />

            <div className="basis-[53%] h-full w-full shrink-0 bg-white shadow-module-card max-h-[75.75vh]">
              {vendorSelected &&
                vendorsListResponse &&
                !isVendorDetailsLoading ? (
                <NiaOppDetailsSection
                  niaBaseDiff={niaBaseDiff}
                  niaBaseOpportunity={niaBaseOpportunity}
                  niaGrowthOpportunity={niaGrowthOpportunity}
                  sliderPercentage={sliderPercentage}
                  commodityFiltersList={commodityFiltersList}
                  selectedCommodityFilter={selectedCommodityFilter}
                  peerBenchmarkList={peerBenchmarkList}
                  bestBenchmark={bestBenchmark}
                  setBestBenchmark={setBestBenchmark}
                  peerBenchmark={peerBenchmark}
                  setPeerBenchmark={setPeerBenchmark}
                  resetHandler={resetHandler}
                  handleSubmitChanges={handleSubmitChanges}
                  handleCategorySelected={handleCategorySelected}
                  selectedBaseRebate={selectedBaseRebate}
                  vendorName={
                    vendorDetailsResponse?.opportunityData[0]?.vendor ||
                    vendorSelected?.vendor
                  }
                  spendYear={vendorsListResponse.spend_year}
                  isPeerBenchmarkDetailsLoading={isPeerBenchmarkDetailsLoading}
                  editMode={editMode}
                  setEditMode={setEditMode}
                  handleExport={handleExport}
                  selectedSpend={selectedSpend}
                  isValidInputBenchmark={isValidInputBenchmark}
                />
              ) : (
                <span className="flex h-full justify-center items-center font-normal text-[3vh]">
                  Select a supplier to view details
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NIAOpportunity;
